import _ from 'lodash';

import { api } from '_/services/api';

import * as types from '_/vuex/mutation-types';

import { store } from '_/vuex/store';

/**
 * PCMS dynamic-module endpoints (POST /api/widgets, POST /api/filter, …)
 * return entries as `{ system: { id, created_at, … }, fields: { … } }`.
 * The widget Vuex state + DashboardWidgets.vue template were written for
 * the legacy flat shape (singleWidget.id, singleWidget.type, …), so we
 * normalise on the way in. Keeps the mutations + template unchanged.
 */
function flattenEntry(entry) {
    if (!entry || typeof entry !== 'object') return entry;
    if (!entry.system && !entry.fields) return entry; // already flat
    const sys = entry.system || {};
    return {
        ...(entry.fields || {}),
        id: sys.id,
        created_at: sys.created_at,
        updated_at: sys.updated_at,
        // Widget template renders `item.anchor_text` as the row title, so
        // hoist the system-level anchor up too.
        anchor_text: sys.anchor_text,
        slug: sys.slug,
    };
}

export default {
    /**
     * Creates new widge
     *
     * @param   {function}  options.commit
     * @param   {function}  options.dispatch
     * @param   {string}  options.type
     * @return  {promise}
     */
    createNewWidget({ commit, dispatch }, { type }) {
        const payload = {
            heading: '',
            icon: 'fal fa-circle',
            meta_data: '',
            module: '',
            private: false,
            refresh_interval: type === 'latest-entries-widget' ? '10000' : '600000',
            theme: 'panel-primary',
            type,
        };

        return dispatch('submitEntry', { payload }).then((response) => {
            commit(types.CREATE_NEW_WIDGET, { widget: flattenEntry(response.entry) });

            return response;
        });
    },

    /**
     * Deletes a widget
     *
     * @param   {function}  options.commit
     * @param   {integer}  options.entryId
     * @return  {promise}
     */
    deleteEntry({ commit }, { entryId }) {
        return api
            .delete(`widgets/${entryId}`)
            .then((response) => {
                commit(types.DELETE_WIDGET, { widgetId: entryId });

                return Promise.resolve(response);
            })
            .catch((response) => {
                return Promise.reject(response);
            });
    },

    /**
     * Gets latest entries from a given module
     *
     * @param   {function}  options.commit
     * @param   {integer}  options.itemsPerPage
     * @param   {string}  options.moduleTableName
     * @return  {promise}
     */
    getLatestEntries({ commit }, { itemsPerPage, moduleTableName }) {
        let payload = {
            include_relations: true,
            pagination: {
                current_page: 1,
                items_per_page: itemsPerPage,
            },
            modules: [moduleTableName],
            // Sort by created_at so the "latest" widget actually shows the
            // most recently CREATED entries. The widget displays
            // `moment(item.created_at).fromNow()` as the row time label, so
            // sorting by created_at keeps the display in sync with the order
            // — sorting by updated_at instead surfaces stale records that
            // were edited today (e.g. a 10-year-old admin row at the top).
            sorting: {
                created_at: 'desc',
            },
        };

        return api.post('filter', payload).then(
            (response) => {
                // PCMS returns `{system, fields}` envelopes; the widget
                // template reads `item.id` / `item.anchor_text` /
                // `item.<column>` directly, so flatten here.
                const data = (response.data.body.entries || []).map(flattenEntry);

                return Promise.resolve(data);
            },
            () => {
                return Promise.reject();
            }
        );
    },

    /**
     * Gets weekly chart data from a given module
     *
     * @param   {function}  options.commit
     * @param   {string}  options.moduleTableName
     * @param   {string}  options.chartPeriod
     * @return  {promise}
     */
    getWeeklyChartData({ commit }, { moduleTableName, chartPeriod }) {
        let payload = {
            chart_period: chartPeriod,
        };

        return api.post(`weekly-chart/${moduleTableName}`, payload).then(
            (response) => {
                const data = response.data.body.data;

                return Promise.resolve(data);
            },
            () => {
                return Promise.reject();
            }
        );
    },

    /**
     * Gets item stats from a given module name
     *
     * @param   {function}  options.commit
     * @param   {string}  options.moduleTableName
     * @return  {promise}
     */
    getItemStats({ commit }, { moduleTableName }) {
        return api.get(`stats/${moduleTableName}`).then(
            (response) => {
                const stats = response.data.body.stats;

                return Promise.resolve(stats);
            },
            () => {
                return Promise.reject();
            }
        );
    },

    /**
     * Gets the widget module data
     *
     * @param   {function}  options.commit
     * @return  {promise}
     */
    getWidgetModuleFields({ commit }) {
        const moduleTableName = 'widgets';

        return store.dispatch('photonModule/getPhotonModuleInformation', { moduleTableName }).then(() => {
            const selectedModuleCopy = Object.assign({}, store.state.photonModule.moduleInformation[moduleTableName]);

            commit(types.UPDATE_WIDGET_MODULE_FIELDS, { fields: selectedModuleCopy.fields });
        });
    },

    /**
     * Gets the selected module data
     *
     * @param   {function}  options.commit
     * @return  {promise}
     */
    getSelectedModuleFields({ commit }, { moduleTableName }) {
        if (!moduleTableName) {
            return Promise.resolve([]);
        }

        return store.dispatch('photonModule/getPhotonModuleInformation', { moduleTableName }).then(() => {
            const selectedModuleCopy = Object.assign({}, store.state.photonModule.moduleInformation[moduleTableName]);

            return selectedModuleCopy.fields;
        });
    },

    /**
     * Retrieves an array of widgets.
     *
     * @param  {function}  options.commit
     * @param  {function}  options.dispatch
     * @return  {promise}
     */
    getWidgets({ commit, dispatch }) {
        const payload = {
            filter: {
                private: {
                    equal: 0,
                },
            },
            modules: ['widgets'],
            include_relations: false,
            sorting: {
                lft: 'asc',
            },
        };

        return api.post('filter', payload).then(
            (response) => {
                dispatch('getPrivateWidgets').then(
                    () => {
                        const values = (response.data.body.entries || []).map(flattenEntry);

                        commit(types.GET_WIDGETS_SUCCESS, { values });

                        return Promise.resolve(values);
                    },
                    () => {
                        return Promise.reject();
                    }
                );
            },
            () => {
                return Promise.reject();
            }
        );
    },

    /**
     * Gets private widgets
     *
     * @param   {function}  options.commit
     * @return  {void}
     */
    getPrivateWidgets({ commit }) {
        const payload = {
            filter: {
                private: {
                    equal: 1,
                },
                created_by: {
                    equal: store.state.user.meta.id,
                },
            },
            modules: ['widgets'],
            include_relations: false,
            sorting: {
                lft: 'asc',
            },
        };

        return api.post('filter', payload).then(
            (response) => {
                const values = (response.data.body.entries || []).map(flattenEntry);

                commit(types.GET_WIDGETS_SUCCESS, { values });

                return Promise.resolve(values);
            },
            () => {
                return Promise.reject();
            }
        );
    },

    /**
     * Resets Widget Model
     *
     * @param   {function}  options.commit
     * @return  {void}
     */
    resetWidgetModel({ commit }) {
        commit(types.RESET_WIDGET_MODEL);
    },

    /**
     * Submits (POST or PUT) entry to API
     *
     * @param   {function}  options.dispatch
     * @param   {object}  options.state
     * @return  {promise}
     */
    submitEntry({ dispatch, state }, { payload }) {
        let action = 'post';

        let uri = 'widgets';

        const entryId = payload.id;

        if (_.has(payload, 'id')) {
            action = 'put';

            uri = `${uri}/${payload.id}`;
        }

        // Split payload into module-field values and overflow that we stash
        // in meta_data. The widget Vue components store widget config (e.g.
        // chartTitle, dataLabel, additional UI state) alongside the canonical
        // module columns; anything not registered as a column on the widgets
        // module is JSON-encoded into the meta_data column.
        const fields = {};
        const metaData = {};
        for (const key in payload) {
            if (key === 'id') {
                continue;
            }
            if (_.find(state.widgetsModuleFields, { column_name: key })) {
                fields[key] = payload[key];
            } else {
                metaData[key] = payload[key];
            }
        }
        fields['meta_data'] = JSON.stringify(metaData);

        // PCMS DynamicModuleController reads `system` + `fields` from the
        // request body (DynamicModuleController.php:396-397). Sending a flat
        // payload causes every field-level validation rule to fire as
        // "required field missing" because the controller sees fields=[] and
        // tries to validate against the empty bag. Envelope before POST/PUT.
        const envelope = action === 'put'
            ? { system: { id: entryId }, fields }
            : { fields };

        return api[action](uri, envelope, [], { Locales: store.state.ui.localesHeaderParameter })
            .then((response) => {
                return Promise.resolve(response.data.body);
            })
            .catch((response) => {
                return Promise.reject(response);
            });
    },

    /**
     * Updates a widget
     *
     * @param   {function}  options.commit
     * @param   {object}  options.widget
     * @return  {void}
     */
    updateWidget({ commit }, { widget }) {
        commit(types.UPDATE_WIDGET, { widget });
    },
};
